import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { AppService } from '../../app.service';
import { ReportsService } from '../../services/reports/reports.service';
import { saveAs } from 'file-saver';
import { forkJoin } from 'rxjs';
import { ReferenceDataService } from '../../services/referencedata/reference-data.service';
import { ModalDialogSeverity } from '../../enums/modal-dialog-severity';

@Component({
  selector: 'app-notification-report',
  templateUrl: './notification-report.component.html',
  styleUrls: ['./notification-report.component.css']
})

export class NotificationReportComponent implements OnInit, OnDestroy {

    public creditorsFormControl: FormControl = new FormControl();
    public statusesFormControl:FormControl = new FormControl();
    public typesFormControl: FormControl = new FormControl();
    public fromDateFormControl: FormControl = new FormControl();
    public toDateFormControl: FormControl = new FormControl();
    public creditorsList: any = null;
    public statusesList: any = null;
    public typesList: any = null;
    public reportData: any = [];
    public reportsListDataSource: any = [];
    public isReportPendingRetrieval: boolean = false;
    public searchParameters: any = null;
    
    public creditors: any | null = null;
    public selectedCreditor: any | null = null;
    public filteredItems: any[] = [];
    public pendingCancellationNotificationMetricsRunIds: number[] = [];

    public constructor(public appService: AppService,
                       public reportsService: ReportsService,
                       public referenceDataService: ReferenceDataService) {
    }

    public ngOnInit(): void {

        this.appService.IsAppBusy = true;

        forkJoin(this.reportsService.getDefaultSearchParameters(),
                 this.reportsService.listAvailableNotificationMetricsRuns(), 
                 this.referenceDataService.getCreditors(),
                 this.referenceDataService.getNotificationStatuses(),
                 this.referenceDataService.getNotificationTypes()).subscribe(([searchParameters,
                                                                               availableReports,
                                                                               creditors,
                                                                               statuses,
                                                                               types]) => {

            var allowedCreditors = this.appService.CurrentSession.AuthenticatedUser.User.Claims.filter((i: any) => i.Type.toLowerCase() == "creditorid").map((i: any) => i.Value);
            var creditorsFiltered = creditors.filter((i: any) => allowedCreditors.indexOf(i.CreditorId) > - 1);
            this.reportsListDataSource = availableReports;
            this.searchParameters = searchParameters;
            this.creditorsList = creditorsFiltered;
            this.statusesList = statuses;
            this.typesList = types;

            this.creditors = creditors.map((i: any) => ({
                ...i,
                LongNameWithId: `${i.CompanyName} (${i.CreditorId})`
            }));

            this.searchParameters.FromDate = new Date(this.searchParameters.FromDate);
            this.searchParameters.ToDate = new Date(this.searchParameters.ToDate);

            this.appService.IsAppBusy = false;

            // start the signalr listener if anything is running at the time of visiting this component 
            const runningNotificationMetricsRuns = this.reportsListDataSource.filter((r: any) => !r.FinishedOnUtc);

            for (let runningNotificationMetricsRun of runningNotificationMetricsRuns) {

                this.reportsService.startProgressConnection(runningNotificationMetricsRun.NotificationMetricsRunId);

                this.reportsService.onProgress(message => {

                    this.refreshAvailableReports();
                });
            }

            // detect if anything is still in the cancelation state waiting to cancel 
            const pendingCancellationNotificationMetricsRuns = this.reportsListDataSource.filter((r: any) => r.CancelRequestedOnUtc != null && !r.FinishedOnUtc);

            for (let pendingCancellationNotificationMetricsRun of pendingCancellationNotificationMetricsRuns) {

                const index = this.pendingCancellationNotificationMetricsRunIds.indexOf(pendingCancellationNotificationMetricsRun.NotificationMetricsRunId);

                if (index == -1) {

                    this.pendingCancellationNotificationMetricsRunIds.push(pendingCancellationNotificationMetricsRun.NotificationMetricsRunId);
                }
            }
        });
    }

    public runReport = () => {

        if (this.searchParameters.CreditorId == '') {

            this.appService.ShowModalDialog(ModalDialogSeverity.Info, "Missing Input", "You didn't enter an account contact id.  Enter an account contact id in thre format creditor/{creditorid}/accountcontact/{integrationlinkid}.  So for example creditor/1/accountcontact/abc123.", "GOTCHA", "");

        } else {

            this.appService.IsAppBusy = true;

            this.reportsService.queueNotificationMetricsReport(this.searchParameters).subscribe((r: any | null) => {

                if (r != null) {

                    var notificationMetricsRunId = r.NotificationMetricsRunId;

                    if (notificationMetricsRunId > 0) {

                        try { // setup signalr

                            this.reportsService.startProgressConnection(notificationMetricsRunId);

                            this.reportsService.onProgress(msg => {

                                this.refreshAvailableReports();
                            });

                        } catch (e) {

                            // 
                        }

                        this.reportsService.runNotificationMetricsReport(notificationMetricsRunId).subscribe((result: any | null) => {

                            if (result != null) {

                                this.appService.IsAppBusy = false;

                                const index = this.pendingCancellationNotificationMetricsRunIds.indexOf(result.NotificationMetricsRunId);

                                if (index !== -1) { // this means the report finished processing as a result of a cancellation request.  remove the tracking so the gui can update appropriately 

                                    this.pendingCancellationNotificationMetricsRunIds.splice(index, 1);
                                }

                                this.refreshAvailableReports();
                            }
                        });
                    }
                }
            });

            setTimeout(() => {

                this.refreshAvailableReports();

            }, 1000);
        }
    }

    public refreshAvailableReports = (): void => {

        this.reportsService.listAvailableNotificationMetricsRuns().subscribe((r: any | null) => {

            if (r) {

                this.reportsListDataSource = r;
            }
        });
    }

    public downloadReport(notificationMetricsRunId: number): void {

        this.reportsService.downloadNotificationMetricsReport(notificationMetricsRunId).subscribe(blob => {

            if (blob != null) {

                const downloadUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');

                a.href = downloadUrl;
                a.download = `NotificationMetricsReport_${notificationMetricsRunId}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(downloadUrl);
            }

        }, error => {

            console.error('Download failed', error);
        });
    }

    public filterItems(event: any) {

        let filtered: any[] = [];
        let query = event.query;

        for (let i = 0; i < (this.creditors as any[]).length; i++) {

            let item = (this.creditors as any[])[i];

            if (item.LongNameWithId.toLowerCase().indexOf(query.toLowerCase()) !== -1) {

                filtered.push(item);
            }
        }

        this.filteredItems = filtered;
    }

    public onCreditorSelected(event: any): void {

        this.searchParameters.CreditorId = event.value.CreditorId;
    }

    public cancelReport = (notificationMetricsRunId: number): void => {

        this.pendingCancellationNotificationMetricsRunIds.push(notificationMetricsRunId);

        this.reportsService.requestNotificationMetricsRunCancellation(notificationMetricsRunId).subscribe((r: any | null) => {

            if (r) {

                const index = this.pendingCancellationNotificationMetricsRunIds.indexOf(notificationMetricsRunId);

                if (index == -1) { // incase the user clicked multiple times, avoid re-adding the tracking id and re-issuing the cancellation request

                    this.pendingCancellationNotificationMetricsRunIds.push(notificationMetricsRunId);
                }
            }
        });
    }

    public refreshReportsList = (): void => {

        this.refreshAvailableReports();
    }

    public ngOnDestroy(): void {

        this.reportsService.stopProgressConnection();
    }
}
